import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import { CalendarCheck, CheckCircle2, Phone, Mail, Loader2 } from 'lucide-react';
import { Reveal, SectionHeading } from './Reveal';
import { useLang } from '../i18n';

const inputCls =
    'w-full rounded-xl border border-ink/15 bg-white px-4 py-3 text-[15px] text-ink placeholder:text-ink/35 transition-colors focus:border-crimson focus:outline-none';

export default function Booking() {
    const { t } = useLang();
    const [form, setForm] = useState({
        nombre: '',
        email: '',
        telefono: '',
        empresa: '',
        servicio: '',
        fecha_preferida: '',
        mensaje: '',
    });
    const [status, setStatus] = useState('idle');

    const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

    const SERVICIOS = [t('opt_growth'), t('opt_seo'), t('opt_sem'), t('opt_branding'), t('opt_digital')];

    const submit = async (e) => {
        e.preventDefault();
        if (status === 'sending') return;
        setStatus('sending');
        try {
            const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/appointments`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(form),
            });
            if (!res.ok) throw new Error('bad response');
            setStatus('success');
            toast.success(t('agenda_ok_title'));
        } catch (err) {
            setStatus('error');
            toast.error(t('agenda_error'));
        }
    };

    const today = new Date().toISOString().split('T')[0];
    const firstName = form.nombre ? `, ${form.nombre.split(' ')[0]}` : '';

    return (
        <section id="agenda" className="py-24 lg:py-32 bg-canvas" data-testid="agenda-section">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <SectionHeading eyebrow={t('agenda_eyebrow')} title={t('agenda_title')} />

                <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8 mt-14">
                    <Reveal className="lg:col-span-5">
                        <aside className="h-full rounded-[1.6rem] bg-ink text-pastelcream p-9 lg:p-10 relative overflow-hidden" data-testid="agenda-info-panel">
                            <div className="absolute -left-14 -bottom-14 w-56 h-56 rounded-full bg-crimson/20 blur-3xl" />
                            <h3 className="font-display text-2xl font-semibold">{t('agenda_info_title')}</h3>
                            <p className="mt-3 text-pastelcream/70 leading-relaxed text-[15px]">
                                {t('agenda_info_sub')}
                            </p>
                            <ul className="mt-9 space-y-5">
                                <li className="flex items-center gap-4" data-testid="agenda-info-phone">
                                    <span className="w-10 h-10 rounded-full bg-pastelcream/10 flex items-center justify-center"><Phone size={16} /></span>
                                    <span className="text-[15px]">667 312 553</span>
                                </li>
                                <li className="flex items-center gap-4" data-testid="agenda-info-email">
                                    <span className="w-10 h-10 rounded-full bg-pastelcream/10 flex items-center justify-center"><Mail size={16} /></span>
                                    <span className="text-[15px]">pichonteam13@gmail.com</span>
                                </li>
                            </ul>
                            <p className="mt-9 font-wordmark text-sm tracking-[0.25em] text-crimson">#PICHONTEAM</p>
                        </aside>
                    </Reveal>

                    <Reveal className="lg:col-span-7" delay={0.12}>
                        <div className="h-full rounded-[1.6rem] bg-white border border-ink/10 p-8 lg:p-10 shadow-[0_30px_60px_-40px_rgba(27,42,74,0.4)]">
                            <AnimatePresence mode="wait">
                                {status === 'success' ? (
                                    <motion.div
                                        key="ok"
                                        initial={{ opacity: 0, scale: 0.96 }}
                                        animate={{ opacity: 1, scale: 1 }}
                                        className="h-full min-h-[420px] flex flex-col items-center justify-center text-center"
                                        data-testid="agenda-success-panel"
                                    >
                                        <span className="w-16 h-16 rounded-full bg-pastelblue flex items-center justify-center">
                                            <CheckCircle2 size={30} className="text-crimson" />
                                        </span>
                                        <h3 className="font-display text-2xl lg:text-3xl font-semibold text-ink mt-6">
                                            {t('agenda_ok_title')}
                                        </h3>
                                        <p className="mt-3 text-ink/70 max-w-sm leading-relaxed">
                                            {t('agenda_ok_text').replace('{name}', firstName)}
                                        </p>
                                        <button
                                            onClick={() => { setForm({ nombre: '', email: '', telefono: '', empresa: '', servicio: '', fecha_preferida: '', mensaje: '' }); setStatus('idle'); }}
                                            className="mt-8 rounded-full border border-ink/20 px-6 py-3 text-[12px] font-bold uppercase tracking-[0.14em] text-ink hover:border-crimson hover:text-crimson transition-colors"
                                            data-testid="agenda-reset-button"
                                        >
                                            {t('agenda_ok_otra')}
                                        </button>
                                    </motion.div>
                                ) : (
                                    <motion.form
                                        key="form"
                                        onSubmit={submit}
                                        initial={{ opacity: 0 }}
                                        animate={{ opacity: 1 }}
                                        exit={{ opacity: 0, y: -12 }}
                                        className="grid grid-cols-1 sm:grid-cols-2 gap-5"
                                        data-testid="agenda-form"
                                    >
                                        <div className="sm:col-span-1">
                                            <label className="text-[11px] font-bold uppercase tracking-[0.16em] text-ink/60">{t('agenda_label_nombre')} *</label>
                                            <input required minLength={2} value={form.nombre} onChange={set('nombre')} placeholder={t('agenda_ph_nombre')} className={`${inputCls} mt-2`} data-testid="agenda-input-nombre" />
                                        </div>
                                        <div className="sm:col-span-1">
                                            <label className="text-[11px] font-bold uppercase tracking-[0.16em] text-ink/60">{t('agenda_label_email')} *</label>
                                            <input required type="email" value={form.email} onChange={set('email')} placeholder="tu@empresa.com" className={`${inputCls} mt-2`} data-testid="agenda-input-email" />
                                        </div>
                                        <div className="sm:col-span-1">
                                            <label className="text-[11px] font-bold uppercase tracking-[0.16em] text-ink/60">{t('agenda_label_telefono')} *</label>
                                            <input required value={form.telefono} onChange={set('telefono')} placeholder="+34 ..." className={`${inputCls} mt-2`} data-testid="agenda-input-telefono" />
                                        </div>
                                        <div className="sm:col-span-1">
                                            <label className="text-[11px] font-bold uppercase tracking-[0.16em] text-ink/60">{t('agenda_label_empresa')} *</label>
                                            <input required value={form.empresa} onChange={set('empresa')} placeholder={t('agenda_ph_empresa')} className={`${inputCls} mt-2`} data-testid="agenda-input-empresa" />
                                        </div>
                                        <div className="sm:col-span-1">
                                            <label className="text-[11px] font-bold uppercase tracking-[0.16em] text-ink/60">{t('agenda_label_servicio')} *</label>
                                            <select required value={form.servicio} onChange={set('servicio')} className={`${inputCls} mt-2 ${form.servicio ? '' : 'text-ink/35'}`} data-testid="agenda-input-servicio">
                                                <option value="" disabled>{t('agenda_ph_servicio')}</option>
                                                {SERVICIOS.map((s) => <option key={s} value={s}>{s}</option>)}
                                            </select>
                                        </div>
                                        <div className="sm:col-span-1">
                                            <label className="text-[11px] font-bold uppercase tracking-[0.16em] text-ink/60">{t('agenda_label_fecha')}</label>
                                            <input type="date" min={today} value={form.fecha_preferida} onChange={set('fecha_preferida')} className={`${inputCls} mt-2`} data-testid="agenda-input-fecha" />
                                        </div>
                                        <div className="sm:col-span-2">
                                            <label className="text-[11px] font-bold uppercase tracking-[0.16em] text-ink/60">{t('agenda_label_mensaje')}</label>
                                            <textarea rows={4} value={form.mensaje} onChange={set('mensaje')} placeholder={t('agenda_ph_mensaje')} className={`${inputCls} mt-2 resize-none`} data-testid="agenda-input-mensaje" />
                                        </div>
                                        <div className="sm:col-span-2 flex flex-col sm:flex-row items-center gap-4">
                                            <button
                                                type="submit"
                                                disabled={status === 'sending'}
                                                className="w-full sm:w-auto inline-flex items-center justify-center gap-2.5 rounded-full bg-crimson px-8 py-4 text-[13px] font-bold uppercase tracking-[0.14em] text-pastelcream transition-all hover:bg-crimsondark hover:scale-[1.02] active:scale-95 disabled:opacity-60"
                                                data-testid="agenda-submit-button"
                                            >
                                                {status === 'sending' ? <Loader2 size={16} className="animate-spin" /> : <CalendarCheck size={16} />}
                                                {status === 'sending' ? t('agenda_btn_enviando') : t('agenda_btn_solicitar')}
                                            </button>
                                            {status === 'error' && (
                                                <p className="text-sm text-crimson font-semibold" data-testid="agenda-error-message">
                                                    {t('agenda_error')}
                                                </p>
                                            )}
                                        </div>
                                    </motion.form>
                                )}
                            </AnimatePresence>
                        </div>
                    </Reveal>
                </div>
            </div>
        </section>
    );
}
